import type { DateRangePreset, DateRangePresetId, DateRangeValue } from "./DateRangePicker.types";

export const CUSTOM_PRESET_ID = "Custom";
export const DAY_MS = 24 * 60 * 60 * 1000;
export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
export const SHORT_MONTH_NAMES = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
export const DAY_HEADS = ["S", "M", "T", "W", "T", "F", "S"];

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  const next = startOfDay(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function compareDay(a: Date, b: Date): number {
  const left = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const right = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return left === right ? 0 : left < right ? -1 : 1;
}

export function differenceInCalendarDays(a: Date, b: Date): number {
  const left = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const right = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round(Math.abs(left - right) / DAY_MS);
}

export function sameDay(a: Date, b?: Date): boolean {
  return Boolean(b && compareDay(a, b) === 0);
}

export function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const shifted = new Date(year, month + delta, 1);
  return { year: shifted.getFullYear(), month: shifted.getMonth() };
}

export function normalizeRange(from: Date, to: Date): { from: Date; to: Date } {
  const start = startOfDay(from);
  const end = startOfDay(to);
  return compareDay(start, end) <= 0 ? { from: start, to: end } : { from: end, to: start };
}

export function defaultFormatDate(date: Date): string {
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

export function presetToRange(preset: DateRangePresetId): { from: Date; to: Date } {
  const today = startOfDay(new Date());

  switch (preset) {
    case "Today":
      return { from: today, to: today };
    case "Yesterday":
      return { from: addDays(today, -1), to: addDays(today, -1) };
    case "Last 7 days":
      return { from: addDays(today, -6), to: today };
    case "Last 30 days":
      return { from: addDays(today, -29), to: today };
    case "This Month":
      return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: today };
    case "Last Month":
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: new Date(today.getFullYear(), today.getMonth(), 0)
      };
    default:
      return { from: today, to: today };
  }
}

export const defaultDateRangePresets: DateRangePreset[] = [
  "Today",
  "Yesterday",
  "Last 7 days",
  "Last 30 days",
  "This Month",
  "Last Month"
].map((id) => ({
  id,
  label: id,
  getRange: () => presetToRange(id)
}));

export function hasCompleteRange(value: DateRangeValue): value is DateRangeValue & { from: Date; to: Date } {
  return value.from instanceof Date && value.to instanceof Date;
}

function toDateOrUndefined(input: unknown): Date | undefined {
  if (input instanceof Date) return Number.isNaN(input.getTime()) ? undefined : input;
  if (typeof input === "string" || typeof input === "number") {
    const parsed = new Date(input);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  }
  return undefined;
}

/**
 * Normalizes an incoming value so `from`/`to` are always real `Date` objects (or
 * `undefined`). Consumers commonly hydrate the value/defaultValue from
 * JSON/API/storage where dates arrive as ISO strings or timestamps; without this
 * the picker crashes on `getFullYear` and never recognizes the range as filled
 * (so the trigger's filled border never lights up).
 */
export function coerceDateRangeValue(value: DateRangeValue): DateRangeValue {
  return { ...value, from: toDateOrUndefined(value.from), to: toDateOrUndefined(value.to) };
}

export function getInitialCalendar(value: DateRangeValue, months: 1 | 2) {
  const base = value.from ?? new Date();
  return months === 2 && !value.from ? shiftMonth(base.getFullYear(), base.getMonth(), -1) : { year: base.getFullYear(), month: base.getMonth() };
}

export function getPresetLabel(presets: DateRangePreset[], presetId?: string) {
  return presets.find((preset) => preset.id === presetId)?.label;
}

